import KoobTitle from '../../../../components/Koob/KoobTitle';
import BulkEditorItem from './BulkEditorItem';
import KoobPlaceholder from '../../../../components/Koob/KoobPlaceholder';
import { useTranslation } from 'react-i18next';
import {
  KoobBadge,
  KoobButton,
  KoobInputSegmented,
  KoobPagination,
} from '@koob/ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useField } from 'formik';
import BulkEditorUpdateMargin from './BulkEditorUpdateMargin';
import { useKoobStore } from '../../../../store';
import { getProductDetails } from '../../utils';

export default function BulkEditor({
  title,
  filters,
  loading,
  results,
  type,
  totalCount,
  filterResult,
  onShowOnlySelectedChange,
  children,
}) {
  const { t } = useTranslation('toConnectionRequest');

  const toConnectionRequestModel = useKoobStore(
    (state) => state.toConnectionRequestModel,
  );
  const [showOnlySelected, setShowOnlySelected] = useState(
    toConnectionRequestModel ? true : null,
  );

  const fieldName = useMemo(() => {
    switch (type) {
      case 'hotel':
        return 'toConnectionRequestHotels';
      case 'experience':
        return 'toConnectionRequestExperiences';
      case 'template':
        return 'toConnectionRequestTemplates';
      default:
        return '';
    }
  }, [type]);

  const [{ value: items }, , { setValue: setItems }] = useField(
    `request.${fieldName}`,
  );

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);
  const [pageDetails, setPageDetails] = useState({});
  const loadedIdsRef = useRef(new Set());

  const filteredResults = useMemo(() => {
    if (showOnlySelected === null) {
      return results;
    }

    return showOnlySelected
      ? items.filter(
          (item) =>
            (item.isSelected === true || item.isSelected === 'true') &&
            (!filterResult || filterResult(item)),
        )
      : results.filter((result) => {
          const matchedItem = items.find(
            (item) => String(item.id) === String(result.id),
          );
          return (
            !matchedItem ||
            (matchedItem.isSelected !== true &&
              matchedItem.isSelected !== 'true')
          );
        });
  }, [results, items, showOnlySelected, filterResult]);

  useEffect(() => {
    setPage(1);
  }, [showOnlySelected, filteredResults.length]);

  const paginatedResults = useMemo(() => {
    if (showOnlySelected !== true) return filteredResults;
    const start = (page - 1) * perPage;
    return filteredResults.slice(start, start + perPage);
  }, [filteredResults, showOnlySelected, page, perPage]);

  const totalPages = useMemo(() => {
    if (showOnlySelected !== true) return undefined;
    return Math.ceil(filteredResults.length / perPage);
  }, [filteredResults.length, showOnlySelected, perPage]);

  const pageItemIds = useMemo(
    () => paginatedResults.map((r) => String(r.id)).join(','),
    [paginatedResults],
  );

  useEffect(() => {
    if (showOnlySelected === null) return;

    const itemsNeedingDetails = paginatedResults.filter(
      (item) => !item.name && !loadedIdsRef.current.has(String(item.id)),
    );

    if (itemsNeedingDetails.length === 0) return;

    itemsNeedingDetails.forEach((item) =>
      loadedIdsRef.current.add(String(item.id)),
    );

    Promise.all(
      itemsNeedingDetails.map(async (item) => {
        try {
          const details = await getProductDetails({ productId: item.id, type });
          return { id: item.id, details };
        } catch {
          return { id: item.id, details: {} };
        }
      }),
    ).then((fetched) => {
      setPageDetails((prev) => {
        const next = { ...prev };
        fetched.forEach(({ id, details }) => {
          next[String(id)] = details;
        });
        return next;
      });
    });
  }, [pageItemIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCount = useMemo(() => {
    return items.filter((i) => {
      const isSelected = i.isSelected === true || i.isSelected === 'true';
      if (!isSelected) return false;
      if (results?.some((r) => String(r.id) === String(i.id))) return true;
      if (!filterResult) return true;
      return filterResult(i);
    }).length;
  }, [items, filterResult, results]);

  const selectAll = () => {
    let newItems = [...items];

    results?.forEach((result) => {
      const findIndex = items.findIndex(
        (selectedItem) => String(selectedItem.id) === String(result.id),
      );

      if (findIndex === -1) {
        newItems.push({
          ...result,
          isMarginPercent: null,
          margin: null,
          distribute: null,
          isSelected: true,
          fromModel: false,
        });
      } else {
        newItems[findIndex] = {
          ...newItems[findIndex],
          isSelected: true,
        };
      }
    });

    setItems(newItems);
  };

  const deselectAll = () => {
    const newItems = items.map((item) => {
      if (results.some((result) => String(result.id) === String(item.id))) {
        return { ...item, isSelected: false };
      }
      return item;
    });

    setItems(newItems);
  };

  return (
    <div className="flex-col space-y-3">
      <div className="grid grid-cols-2 gap-5">
        <div>
          <KoobTitle>{title}</KoobTitle>

          <div className="mt-3 flex space-x-3 items-stretch">{filters}</div>

          <div className="pt-5 flex space-x-3 items-center">
            <KoobButton
              variant="primary"
              onClick={() => selectAll()}
              size="sm"
              disabled={
                !results?.some((result) => {
                  const item = items.find(
                    (i) => String(i.id) === String(result.id),
                  );
                  return (
                    !item ||
                    (item.isSelected !== true && item.isSelected !== 'true')
                  );
                })
              }
            >
              {t('bulkEdit.requestSelector.selectAll')}
            </KoobButton>

            <KoobButton
              variant="primary"
              onClick={() => deselectAll()}
              size="sm"
              disabled={
                !results?.some((result) => {
                  const item = items.find(
                    (i) => String(i.id) === String(result.id),
                  );
                  return (
                    item &&
                    (item.isSelected === true || item.isSelected === 'true')
                  );
                })
              }
            >
              {t('bulkEdit.requestSelector.deselectAll')}
            </KoobButton>

            <KoobBadge color="gray" shape="square">
              {t('bulkEdit.requestSelector.selectedCount', {
                count: selectedCount,
                total: totalCount,
              })}
            </KoobBadge>

            <KoobInputSegmented
              size="sm"
              options={[
                { label: t('bulkEdit.editor.filters.all'), value: null },
                { label: t('bulkEdit.editor.filters.selected'), value: true },
                {
                  label: t('bulkEdit.editor.filters.notSelected'),
                  value: false,
                },
              ]}
              value={showOnlySelected}
              onChange={(value) => {
                setShowOnlySelected(value);
                onShowOnlySelectedChange?.(value);
              }}
            />
          </div>
        </div>

        <BulkEditorUpdateMargin
          fieldName={fieldName}
          results={filteredResults}
          filterResult={filterResult}
          showOnlySelected={showOnlySelected}
        />
      </div>

      <div className="h-[80vh] overflow-scroll bg-gray-50 p-5 rounded-md">
        {paginatedResults?.length > 0 || loading ? (
          <div className="flex-col space-y-3">
            {loading && (
              <>
                <KoobPlaceholder className="h-28 w-full rounded-md" />
                <KoobPlaceholder className="h-28 w-full rounded-md" />
                <KoobPlaceholder className="h-28 w-full rounded-md" />
                <KoobPlaceholder className="h-28 w-full rounded-md" />
                <KoobPlaceholder className="h-28 w-full rounded-md" />
                <KoobPlaceholder className="h-28 w-full rounded-md" />
              </>
            )}

            {!loading &&
              paginatedResults?.map((result) => {
                const fetched = pageDetails[String(result.id)];
                const item = fetched ? { ...result, ...fetched } : result;
                return item.name ? (
                  <BulkEditorItem key={result.id} item={item} type={type} />
                ) : (
                  <KoobPlaceholder
                    key={result.id}
                    className="h-28 w-full rounded-md"
                  />
                );
              })}
          </div>
        ) : (
          <div className="h-96 flex flex-col justify-center items-center">
            <div className="text-center">
              <i className="far fa-search text-4xl text-gray-300" />

              <div className="mt-3 font-medium text-gray-500">
                {t('bulkEdit.editor.noResults')}
              </div>
              <div className="text-sm text-gray-500">
                {t('bulkEdit.editor.noResultsDescription')}
              </div>
            </div>
          </div>
        )}
      </div>

      {showOnlySelected === true ? (
        <KoobPagination
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={filteredResults.length}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      ) : (
        <div>{children}</div>
      )}
    </div>
  );
}
